import * as React from "react";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { AnalyticsSpinner } from "../shared";

/**
 * RevenueCalendar Component
 * 
 * A FullCalendar-based component that displays expected daily subscription revenue
 * and actual billing data with visual status indicators.
 */

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  color: string;
  extendedProps: {
    expected_amount: number;
    actual_amount: number;
    status: string;
    billing_events_count: number;
    type: 'revenue_day' | 'actual_payment';
  };
}

interface BillingEvent {
  date: string;
  subscription_id: string;
  customer_email: string;
  plan_name: string;
  expected_amount: number;
  actual_amount: number;
  status: 'paid' | 'pending';
  invoice_id?: string;
  billing_reason: string;
}

interface RevenueCalendarData {
  events: CalendarEvent[];
  billing_events: BillingEvent[];
  date_range: {
    start: string;
    end: string;
  };
  summary: {
    total_expected: number;
    total_actual: number;
    billing_days: number;
    completed_days: number;
  };
}

interface RevenueCalendarProps {
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  height?: number;
  onDateClick?: (date: string) => void;
  onEventClick?: (event: CalendarEvent) => void;
  filters?: {
    plan_filter?: string;
    utm_source_filter?: string;
  };
}

interface VisibleDateRange {
  start_date: string;
  end_date: string;
}

// Reasonable default for the very first fetch, before FullCalendar has mounted and
// reported its actual rendered grid via datesSet: the current calendar month. This
// already includes any future days in the current month (unlike the dashboard's old
// "90 days ago -> today" filters, which never requested future dates at all). Once
// FullCalendar mounts, datesSet reports the true grid (which pads to full weeks) and
// the range is refined.
// Format using LOCAL date parts, not .toISOString() -- toISOString() converts to UTC
// first, which rolls local midnight back to the previous day for any UTC+ timezone
// (e.g. local Aug 1 00:00 in UTC+5 is July 31 19:00 UTC), silently shrinking this
// range by a day on the transient first fetch.
const formatLocalDate = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = (): VisibleDateRange => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start_date: formatLocalDate(start), end_date: formatLocalDate(end) };
};

const RevenueCalendar: React.FC<RevenueCalendarProps> = ({
  isLoading = false,
  error = null,
  className = "",
  height = 600,
  onDateClick,
  onEventClick,
  filters = {}
}) => {
  const [calendarData, setCalendarData] = React.useState<RevenueCalendarData | null>(null);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [currentView, setCurrentView] = React.useState('dayGridMonth');
  // The calendar owns its own date range, driven by whatever FullCalendar is actually
  // showing (see handleDatesSet below) -- NOT the dashboard's start_date/end_date filters,
  // which default to "90 days ago -> today" and would never include future billing dates.
  const [visibleRange, setVisibleRange] = React.useState<VisibleDateRange>(getCurrentMonthRange);
  const calendarRef = React.useRef<FullCalendar>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { plan_filter: planFilter, utm_source_filter: utmSourceFilter } = filters;
  const hasCalendarData = calendarData !== null;

  // The Rails report mounts this component in a hidden Bootstrap tab. Re-measure
  // after it becomes visible, and when the sidebar or viewport changes its width.
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let frame = 0;
    let previousWidth = 0;
    const resizeCalendar = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const width = container.getBoundingClientRect().width;
        if (width > 0 && width !== previousWidth) calendarRef.current?.getApi().updateSize();
        previousWidth = width;
      });
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resizeCalendar) : null;
    observer?.observe(container);
    document.addEventListener('shown.bs.tab', resizeCalendar);
    resizeCalendar();

    return () => {
      observer?.disconnect();
      document.removeEventListener('shown.bs.tab', resizeCalendar);
      window.cancelAnimationFrame(frame);
    };
  }, [hasCalendarData]);

  // Fetch calendar data
  const fetchCalendarData = React.useCallback(async () => {
    setIsLoadingData(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();

      // Add date range parameters -- always the calendar's own visible range.
      params.append('start_date', visibleRange.start_date);
      params.append('end_date', visibleRange.end_date);
      if (planFilter) params.append('plan_filter', planFilter);
      if (utmSourceFilter) params.append('utm_source_filter', utmSourceFilter);

      const url = `/admin/analytics/subscriptions/revenue_calendar.json?${params}`;

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCalendarData(data);
    } catch (err) {
      setCalendarData(null);
      setFetchError(err instanceof Error ? err.message : 'Failed to load revenue calendar data');
    } finally {
      setIsLoadingData(false);
    }
  }, [visibleRange, planFilter, utmSourceFilter]);

  // Fetch data on mount, when the visible range changes (month navigation), or when
  // the plan/utm filters change.
  React.useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // FullCalendar reports the range it actually rendered (activeStart/activeEnd) every
  // time the view mounts or the user navigates months. activeEnd is EXCLUSIVE (the day
  // after the last visible day); convert to an inclusive end date to match the backend's
  // start_date/end_date contract. Guarded with a value comparison before setState so an
  // identical range (e.g. FullCalendar re-reporting the same view) never triggers a
  // redundant re-render/refetch loop.
  const handleDatesSet = React.useCallback((arg: any) => {
    setCurrentView(arg.view.type);

    const startDate = arg.startStr.split('T')[0];
    const inclusiveEnd = new Date(arg.endStr);
    inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
    const endDate = inclusiveEnd.toISOString().split('T')[0];

    setVisibleRange((prev) => {
      if (prev.start_date === startDate && prev.end_date === endDate) {
        return prev;
      }
      return { start_date: startDate, end_date: endDate };
    });
  }, []);

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Handle date click
  const handleDateClick = (dateInfo: any) => {
    const dateString = dateInfo.dateStr;
    if (onDateClick) {
      onDateClick(dateString);
    }
  };

  // Handle event click
  const handleEventClick = (eventInfo: any) => {
    const event = eventInfo.event;
    const calendarEvent: CalendarEvent = {
      id: event.id,
      title: event.title,
      start: event.startStr,
      end: event.endStr,
      allDay: event.allDay,
      color: event.backgroundColor,
      extendedProps: event.extendedProps
    };
    
    if (onEventClick) {
      onEventClick(calendarEvent);
    }
  };

  // Render event content with custom styling
  const renderEventContent = (eventInfo: any) => {
    const { event } = eventInfo;
    const props = event.extendedProps;
    
    return (
      <div className="fc-event-content-wrapper">
        <div className="fc-event-title-container">
          <div className="fc-event-title">{event.title}</div>
          {props.type === 'revenue_day' && props.billing_events_count > 0 && (
            <small className="fc-event-subtitle">
              {props.billing_events_count} subscription{props.billing_events_count !== 1 ? 's' : ''}
            </small>
          )}
          {props.actual_amount > 0 && props.type === 'revenue_day' && (
            <small className="fc-event-subtitle">
              Paid: {formatCurrency(props.actual_amount)}
            </small>
          )}
        </div>
      </div>
    );
  };

  // Get status legend
  const getStatusLegend = () => [
    { status: 'completed', color: '#28a745', label: 'Billing Completed' },
    { status: 'partial', color: '#ffc107', label: 'Partial Payment' },
    { status: 'pending', color: '#dc3545', label: 'Pending/Overdue' },
    { status: 'future', color: '#6c757d', label: 'Future Billing' },
  ];

  const displayError = error || fetchError;

  if (displayError) {
    return (
      <div className={`revenue-calendar ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-calendar-alt me-2" aria-hidden="true"></i>
              Revenue Calendar
            </h6>
          </div>
          <div className="card-body">
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error:</strong> {displayError}
              {!error && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-3"
                  onClick={fetchCalendarData}
                >
                  <i className="fas fa-redo me-1" aria-hidden="true"></i>
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isLoading || isLoadingData) && !calendarData) {
    return (
      <div className={`revenue-calendar ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-calendar-alt me-2" aria-hidden="true"></i>
              Revenue Calendar (Loading...)
            </h6>
          </div>
          <div className="card-body">
            <AnalyticsSpinner
              height={height}
              message="Loading revenue calendar..."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Calendar - render directly without card wrapper */}
      {calendarData ? (
            <div ref={containerRef} style={{ height: `${height}px` }} aria-busy={isLoading || isLoadingData}>
              {(isLoading || isLoadingData) && <p role="status" className="text-muted small">Updating revenue calendar...</p>}
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView={currentView}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                customButtons={{
                  refresh: {
                    text: 'Refresh',
                    click: fetchCalendarData
                  }
                }}
                events={calendarData.events}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventContent={renderEventContent}
                height={height}
                dayMaxEvents={3}
                moreLinkClick="popover"
                selectable={false}
                selectMirror={false}
                editable={false}
                droppable={false}
                eventStartEditable={false}
                eventDurationEditable={false}
                eventDisplay="block"
                displayEventTime={false}
                view={currentView}
                datesSet={handleDatesSet}
              />
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-calendar-alt fa-2x mb-3" aria-hidden="true"></i>
          <p className="mb-0">No revenue data available for the selected period</p>
        </div>
      )}

      <style jsx>{`
        .revenue-calendar .fc-event {
          border: none;
          font-size: 0.8rem;
        }

        .revenue-calendar .fc-event-title {
          font-weight: 600;
          font-size: 0.75rem;
        }

        .revenue-calendar .fc-event-subtitle {
          display: block;
          font-size: 0.65rem;
          opacity: 0.8;
          margin-top: 1px;
        }

        .revenue-calendar .fc-daygrid-event {
          margin: 1px;
          padding: 2px 4px;
          border-radius: 3px;
        }

        .revenue-calendar .fc-list-event {
          cursor: pointer;
        }

        .revenue-calendar .fc-list-event:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .revenue-calendar .btn-group .btn-sm {
          padding: 0.25rem 0.5rem;
          font-size: 0.8rem;
        }

        .revenue-calendar .btn-check:checked + .btn {
          background-color: var(--bs-primary);
          border-color: var(--bs-primary);
          color: white;
        }

        @media (max-width: 768px) {
          .revenue-calendar .card-header .d-flex {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .revenue-calendar .btn-group {
            width: 100%;
          }

          .revenue-calendar .btn-group .btn-sm {
            flex: 1;
          }
        }
      `}</style>
    </>
  );
};

RevenueCalendar.displayName = 'RevenueCalendar';

export default RevenueCalendar;
export type { CalendarEvent, BillingEvent, RevenueCalendarData };
// Exported for direct unit testing of the local-date-parts formatting (regression guard
// against reintroducing .toISOString()-based formatting, which UTC-shifts the date for
// any non-zero timezone offset).
export { formatLocalDate };
